const LANGS = {
    ts: () => import('@shikijs/langs/typescript'),
    typescript: () => import('@shikijs/langs/typescript'),
    tsx: () => import('@shikijs/langs/tsx'),
    js: () => import('@shikijs/langs/javascript'),
    javascript: () => import('@shikijs/langs/javascript'),
    jsx: () => import('@shikijs/langs/jsx'),
    json: () => import('@shikijs/langs/json'),
    bash: () => import('@shikijs/langs/bash'),
    sh: () => import('@shikijs/langs/bash'),
    shell: () => import('@shikijs/langs/bash'),
    zsh: () => import('@shikijs/langs/bash'),
    css: () => import('@shikijs/langs/css'),
    html: () => import('@shikijs/langs/html'),
    md: () => import('@shikijs/langs/markdown'),
    markdown: () => import('@shikijs/langs/markdown'),
    python: () => import('@shikijs/langs/python'),
    py: () => import('@shikijs/langs/python'),
    swift: () => import('@shikijs/langs/swift'),
    rust: () => import('@shikijs/langs/rust'),
    go: () => import('@shikijs/langs/go'),
    yaml: () => import('@shikijs/langs/yaml'),
    yml: () => import('@shikijs/langs/yaml'),
    diff: () => import('@shikijs/langs/diff'),
    sql: () => import('@shikijs/langs/sql'),
    csharp: () => import('@shikijs/langs/csharp'),
    cs: () => import('@shikijs/langs/csharp'),
    toml: () => import('@shikijs/langs/toml'),
};
const ALIASES = {
    ts: 'typescript',
    js: 'javascript',
    sh: 'bash',
    shell: 'bash',
    zsh: 'bash',
    md: 'markdown',
    py: 'python',
    yml: 'yaml',
    cs: 'csharp',
};
/** Sentinel colour → syntax role token. Anything else renders in the surrounding text colour. */
const ROLE = {
    '#010101': 'var(--vk-tk-k)',
    '#020202': 'var(--vk-tk-s)',
    '#030303': 'var(--vk-tk-n)',
    '#040404': 'var(--vk-tk-c)',
    '#050505': 'var(--vk-tk-f)',
};
/** The Viko syntax theme: scopes → roles (sentinel colours, mapped through `ROLE`). */
export const VIKO_THEME = {
    name: 'viko',
    type: 'dark',
    fg: '#000000',
    bg: '#000000',
    colors: { 'editor.foreground': '#000000', 'editor.background': '#000000' },
    tokenColors: [
        { settings: { foreground: '#000000', background: '#000000' } },
        {
            scope: [
                'keyword',
                'storage',
                'storage.type',
                'storage.modifier',
                'keyword.operator.new',
                'keyword.operator.expression',
                'keyword.control',
                'variable.language',
            ],
            settings: { foreground: '#010101' },
        },
        {
            scope: ['string', 'string.template', 'punctuation.definition.string', 'markup.inline.raw'],
            settings: { foreground: '#020202' },
        },
        {
            scope: ['constant.numeric', 'constant.language', 'constant.character', 'support.constant'],
            settings: { foreground: '#030303' },
        },
        {
            scope: ['comment', 'punctuation.definition.comment'],
            settings: { foreground: '#040404', fontStyle: 'italic' },
        },
        {
            scope: [
                'entity.name.function',
                'support.function',
                'meta.function-call entity.name.function',
                'entity.name.type',
                'entity.name.class',
                'support.type',
                'support.class',
            ],
            settings: { foreground: '#050505' },
        },
    ],
};
const role = (color) => (color ? ROLE[color.toLowerCase()] : undefined);
let highlighter = null;
const loaded = new Map();
function getHighlighter() {
    highlighter ??= (async () => {
        const [{ createHighlighterCore }, { createJavaScriptRegexEngine }] = await Promise.all([
            import('shiki/core'),
            import('shiki/engine/javascript'),
        ]);
        return createHighlighterCore({
            themes: [VIKO_THEME],
            langs: [],
            engine: createJavaScriptRegexEngine(),
        });
    })();
    return highlighter;
}
export function isHighlightable(lang) {
    return !!lang && lang.toLowerCase() in LANGS;
}
export async function highlight(code, lang) {
    const key = lang.toLowerCase();
    const loader = LANGS[key];
    if (!loader)
        return null;
    const h = await getHighlighter();
    const name = ALIASES[key] ?? key;
    if (!loaded.has(name)) {
        loaded.set(name, loader().then((m) => h.loadLanguage(m.default)));
    }
    await loaded.get(name);
    // Shiki's default 500 ms per-line limit is hit on the first lines while the grammar's regexes
    // compile, and the rest of such a line then comes back as one token (one colour). A generous
    // limit keeps the first block right; later lines take microseconds.
    const res = h.codeToTokens(code, { lang: name, theme: 'viko', tokenizeTimeLimit: 5000 });
    return res.tokens.map((line) => line.map((t) => {
        const c = role(t.color);
        return { content: t.content, ...(c ? { light: c, dark: c } : {}), fontStyle: t.fontStyle };
    }));
}
//# sourceMappingURL=highlight.js.map